import { Typography } from 'lighthouse';

import Counter from '@/widgets/Counter';
import { SafeImage } from '@/widgets/SafeImage';

import { rialToToman } from '@/utilities/Financial';
import { GenerateFilePreviewUrl } from '@/utilities/GenerateFilePreviewUrl';
import { withSeparator } from '@/utilities/withSeparator';

import { useCartStore } from '@/stores/cart/useCartStore';

import PriceAdjustments from '../PriceAdjustments';

import { CartItem } from '@/client/services/vendor/models';

const MinimalProductCard = ({ data, cartId, compact = false }: { data: CartItem; cartId: string; compact?: boolean }) => {
    const updateCartItem = useCartStore((state) => state.updateCartItem);
    const deleteCartItem = useCartStore((state) => state.deleteCartItem);
    const isUpdateCartItemLoading = useCartStore((state) => state.isUpdateCartItemLoading);
    const UpdateCartItemId = useCartStore((state) => state.UpdateCartItemId);
    const quantity = data.quantity;
    const isUpdating = isUpdateCartItemLoading && UpdateCartItemId === data.itemId;
    const hasDiscount = data.discountAmount > 0 && data.itemTotal < data.unitPrice * quantity;
    const counterMax = data.availableQuantity >= 0 ? quantity + data.availableQuantity : undefined;

    const handleCounterChange = async (newQuantity: number) => {
        if (isUpdating || newQuantity === quantity) {
            return;
        }

        const queryParam = {
            cartId: cartId,
            id: data.itemId,
            offerId: Number(data.offerId),
            quantity: newQuantity,
            currentQuantity: quantity
        };

        try {
            if (quantity === 1 && newQuantity === 0) {
                await deleteCartItem(queryParam);
            } else {
                await updateCartItem(queryParam);
            }
        } catch {
            // store rolls quantity back on failure
        }
    };

    if (compact) {
        return (
            <div className='bg-white'>
                <div className='flex items-start gap-3 p-3'>
                    <figure className='relative m-0 h-12 w-12 shrink-0 overflow-hidden rounded-md bg-gray-50'>
                        <SafeImage
                            src={
                                GenerateFilePreviewUrl({
                                    attachableId: data.catalogProductUuid,
                                    attachment: 'PRODUCT',
                                    type: 'MAIN',
                                    thumbnail: true,
                                    isPrimary: true,
                                    sortOrder: 0
                                }).src
                            }
                            fallback='/images/noImage/noImage.png'
                            alt={data.title}
                            fill
                            sizes='48px'
                            className='object-contain p-0.5'
                        />
                    </figure>

                    <div className='min-w-0 flex-1'>
                        <Typography className='line-clamp-2 text-xs font-semibold leading-5 text-blackHighEmphasis'>{data.title}</Typography>
                        {data.subtitle && <Typography className='mt-0.5 line-clamp-1 text-[11px] text-gray-500'>{data.subtitle}</Typography>}
                        <div className='mt-2 flex items-end justify-between gap-2'>
                            <Counter isLoading={isUpdating} disabled={isUpdating} max={counterMax} value={quantity} onChange={handleCounterChange} />
                            <div className='flex flex-col items-end'>
                                {hasDiscount && (
                                    <Typography component='span' className='text-[10px] text-gray-400 line-through'>
                                        {withSeparator(rialToToman(data.unitPrice * quantity))}
                                    </Typography>
                                )}
                                <Typography component='span' className='text-sm font-bold text-primary'>
                                    {withSeparator(rialToToman(data.itemTotal))}
                                    <span className='text-[10px] font-normal text-blackHighEmphasis'>&nbsp;تومان</span>
                                </Typography>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='!border-b !border-l-0 !border-r-0 !border-t-0 border-solid !border-gray-800/10 bg-white'>
            <div className='flex items-start gap-3 p-3'>
                <figure className='relative m-0 h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-gray-50'>
                    <SafeImage
                        src={
                            GenerateFilePreviewUrl({
                                attachableId: data.catalogProductUuid,
                                attachment: 'PRODUCT',
                                type: 'MAIN',
                                thumbnail: true,
                                isPrimary: true,
                                sortOrder: 0
                            }).src
                        }
                        fallback='/images/noImage/noImage.png'
                        alt={data.title}
                        fill
                        sizes='64px'
                        className='object-contain p-0.5'
                    />
                </figure>

                <div className='min-w-0 flex-1'>
                    <Typography className='line-clamp-1 text-sm font-semibold text-blackHighEmphasis'>{data.title}</Typography>
                    {data.subtitle && <Typography className='mt-1 line-clamp-1 text-xs text-gray-500'>{data.subtitle}</Typography>}
                    <PriceAdjustments adjustments={data.priceAdjustments} />
                </div>
            </div>

            <div
                className='flex items-end justify-between gap-3 !border-b-0 !border-l-0 !border-r-0 !border-t border-solid !border-gray-300/20
                    bg-gray-50 p-3'>
                <Counter isLoading={isUpdating} disabled={isUpdating} max={counterMax} value={quantity} onChange={handleCounterChange} />

                <div className='flex flex-col items-end'>
                    {hasDiscount && (
                        <Typography component='span' className='text-xs text-gray-400 line-through'>
                            {withSeparator(rialToToman(data.unitPrice * quantity))} تومان
                        </Typography>
                    )}
                    <Typography component='span' className='text-base font-bold text-primary'>
                        {withSeparator(rialToToman(data.itemTotal))}
                        <span className='text-xs font-normal text-blackHighEmphasis'>&nbsp;تومان</span>
                    </Typography>
                </div>
            </div>
        </div>
    );
};

export default MinimalProductCard;
